import { useEffect, useRef, useState } from "react";
import { Ticket, RefreshCw } from "lucide-react";
import {
  QTY_OPTIONS, DEFAULT_QTY, STATUS_COPY, quoteLines, seatingLabel,
  createLatestGuard, fetchJsonNoStore, normalizeResult,
} from "@/lib/ticketOffers";

function OfferRow({ o }) {
  return (
    <li className="rounded-xl border border-border p-4" data-testid={`ticket-offer-${o.ticket_id}`}>
      <div className="font-heading font-bold">{o.category_name || o.ticket_title}</div>
      <div className="text-xs text-foreground/60 mt-1">
        {[o.sub_category, o.eventdays, o.ticket_validity].filter(Boolean).join(" · ")}
      </div>
      <div className="text-sm mt-2">{seatingLabel(o.seating)}</div>
      {quoteLines(o.price).map((l) => <div key={l} className="text-sm font-semibold mt-1">{l}</div>)}
    </li>
  );
}

function ResultView({ result }) {
  if (result.status !== "available") {
    return <p className="text-sm text-foreground/70" data-testid={`ticket-state-${result.status}`}>{STATUS_COPY[result.status]}</p>;
  }
  return (
    <div data-testid="ticket-state-available">
      <p className="text-xs text-foreground/60 mb-3" data-testid="ticket-checked-at">
        Checked {new Date(result.checked_at).toLocaleString()} · XS2Event provider data, not a confirmed checkout
        {!result.prices_shown && " · prices pending confirmation"}
        {result.test_environment && " · TEST environment"}
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">{result.offers.map((o) => <OfferRow key={o.ticket_id} o={o} />)}</ul>
    </div>
  );
}

function useTicketOffers(slug, qty) {
  const [result, setResult] = useState(null);
  const guard = useRef(createLatestGuard());
  const ctrl = useRef(null);
  useEffect(() => {
    ctrl.current?.abort();
    guard.current.next();
    setResult(null);
  }, [slug, qty]);
  useEffect(() => {
    const g = guard.current;
    return () => {
      ctrl.current?.abort();
      g.next();
    };
  }, []);
  const check = async () => {
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    const id = guard.current.next();
    setResult({ status: "loading", offers: [] });
    try {
      const data = await fetchJsonNoStore(`/events/${encodeURIComponent(slug)}/ticket-offers?quantity=${qty}`, ctrl.current.signal);
      if (guard.current.isCurrent(id)) setResult(normalizeResult(data));
    } catch (err) {
      if (guard.current.isCurrent(id) && err.name !== "AbortError") setResult({ status: "error", offers: [] });
    }
  };
  return { result, check };
}

function useProviderStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const c = new AbortController();
    fetchJsonNoStore("/xs2event/status", c.signal)
      .then((d) => setStatus(d?.enabled === true ? "on" : d?.enabled === false ? "off" : "unavailable"))
      .catch((err) => { if (err.name !== "AbortError") setStatus("unavailable"); });
    return () => c.abort();
  }, []);
  return status;
}

export function TicketAvailability({ slug }) {
  const status = useProviderStatus();
  const enabled = status === "on";
  const [qty, setQty] = useState(DEFAULT_QTY);
  const { result, check } = useTicketOffers(slug, qty);
  const loading = result?.status === "loading";
  return (
    <section className="rounded-2xl border border-border p-6 mb-12" data-testid="ticket-availability">
      <h2 className="font-heading text-base md:text-lg font-bold inline-flex items-center gap-2"><Ticket className="h-5 w-5" />Ticket availability (XS2Event)</h2>
      {status === "off" && <p className="text-sm text-foreground/70 mt-2" data-testid="ticket-state-disabled">{STATUS_COPY.disabled}</p>}
      {status === "unavailable" && <p className="text-sm text-foreground/70 mt-2" data-testid="ticket-state-status-unavailable">{STATUS_COPY.unavailable}</p>}
      {enabled && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm" htmlFor="ticket-qty">Tickets</label>
            <select id="ticket-qty" data-testid="ticket-quantity-select" value={qty} disabled={loading}
              onChange={(ev) => setQty(Number(ev.target.value))} className="rounded-md border border-border bg-background px-3 py-2 text-sm">
              {QTY_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button type="button" onClick={check} disabled={loading} data-testid="ticket-check-button"
              className="inline-flex items-center gap-2 rounded-full border border-foreground px-4 py-2 text-sm font-semibold transition-colors hover:bg-foreground hover:text-background disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Check XS2Event availability
            </button>
          </div>
          {result && <ResultView result={result} />}
          <p className="text-xs text-foreground/50">Read-only availability info. Hotelstrips does not sell tickets on this page.</p>
        </div>
      )}
    </section>
  );
}
